import { useCallback, useEffect, useRef, useState } from "react";
import type { ConfirmOptions } from "@gui/widgets/ConfirmDialog";
import type { ConfirmFn } from "./ConfirmContext.types";

interface PendingConfirm {
	options: ConfirmOptions;
	resolve: (answer: boolean) => void;
	answered: boolean;
	/** Its dialog has begun to open, so it will fade out (and call onExited) when it closes. */
	entered: boolean;
}

/** What the provider's one dialog shows: the head of the queue, open until it is answered. */
interface ShownConfirm extends ConfirmOptions {
	open: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	onEnter: () => void;
	onExited: () => void;
}

/**
 * The confirm queue behind `ConfirmProvider`. Requests are shown one at a time, first in first out.
 * Answering one closes the dialog but keeps its text; the next is shown only once the dialog has
 * faded out (`onExited`), so the fade never shows the next request's text.
 *
 * The queue lives in a ref, written only by the callbacks; `shown` is the snapshot the dialog
 * renders. So `confirm` never changes identity, and a queue change re-renders the provider only.
 */
export function useConfirmProviderViewModel(): { confirm: ConfirmFn; dialog: ShownConfirm | null } {
	const queue = useRef<PendingConfirm[]>([]);
	const [shown, setShown] = useState<{ options: ConfirmOptions; open: boolean } | null>(null);

	const sync = useCallback(() => {
		const head = queue.current[0];
		setShown(head ? { options: head.options, open: !head.answered } : null);
	}, []);

	const answer = useCallback(
		(request: PendingConfirm, value: boolean) => {
			if (request.answered) return;
			request.answered = true;
			request.resolve(value);
			// The showing request leaves when its dialog has faded out. Any other (one still waiting,
			// or one whose dialog never began to open and so will never fade out) leaves now.
			if (queue.current[0] !== request || !request.entered) {
				queue.current = queue.current.filter((r) => r !== request);
			}
			sync();
		},
		[sync],
	);

	const confirm = useCallback<ConfirmFn>(
		(options, signal) =>
			new Promise<boolean>((resolve) => {
				if (signal?.aborted) {
					resolve(false);
					return;
				}
				const request: PendingConfirm = { options, resolve, answered: false, entered: false };
				queue.current = [...queue.current, request];
				signal?.addEventListener("abort", () => answer(request, false), { once: true });
				sync();
			}),
		[answer, sync],
	);

	const answerShown = useCallback(
		(value: boolean) => {
			const head = queue.current[0];
			if (head) answer(head, value);
		},
		[answer],
	);
	const onConfirm = useCallback(() => answerShown(true), [answerShown]);
	const onCancel = useCallback(() => answerShown(false), [answerShown]);
	const onEnter = useCallback(() => {
		const head = queue.current[0];
		if (head) head.entered = true;
	}, []);
	const onExited = useCallback(() => {
		if (!queue.current[0]?.answered) return;
		queue.current = queue.current.slice(1);
		sync();
	}, [sync]);

	// Nothing is left waiting on a dialog that can no longer be shown.
	useEffect(
		() => () => {
			for (const request of queue.current) {
				request.answered = true;
				request.resolve(false);
			}
			queue.current = [];
		},
		[],
	);

	return {
		confirm,
		dialog: shown && { ...shown.options, open: shown.open, onConfirm, onCancel, onEnter, onExited },
	};
}
